import clsx from 'clsx';
import Image from 'next/image';
import type { FC, ReactNode } from 'react';

import Title from '@/c/common/Title';
import NavBar from '@/c/common/NavBar';
import Footer from '@/c/common/Footer';
import GenericButton from '@/c/generic/GenericButton';
import GenericDividingTitle from '@/c/generic/GenericDividingTitle';

import mediumIcon from '@/assets/medium-square-icon.svg';
import youtubeIcon from '@/assets/youtube-icon.svg';
import facebookIcon from '@/assets/facebook-icon.svg';
import instagramIcon from '@/assets/instagram-icon.svg';

const ContactUsSection = () => (
  <div className="">
    <GenericDividingTitle>聯絡我們</GenericDividingTitle>
    <div className="px-2 py-4 text-center space-y-2">
      <p>看了這麼曼妙的空間，還是沒有頭緒嗎？</p>
      <p>不要懷疑，快跟我們聯絡吧。</p>
      <p>奧立佛 x 竺居聯合設計</p>
      <p>給您最有溫度、情感及闡釋多元視角的好設計。</p>
      <div className="pt-4">
        <GenericButton
          href="/inquiry"
          variant="secondary"
        >
          立即免費評估
        </GenericButton>
      </div>
    </div>
  </div>
);

interface OfficeLocationItemProps {
  city: '高雄・Kaohsiung' | '台北・Taipei',
  phone: '(07) 2229-568' | '(02) 2737-2727',
  address: '高雄市新興區中正三31號2樓' | '台北市信義區嘉興街127巷4號1樓',
  placeId: 'ChIJYTOSA5IEbjQRkpgsGmSYY_Y' | 'ChIJKVgoGTOrQjQR-IgK6UJ8DY4',
}
const OfficeLocationItem: FC<OfficeLocationItemProps> = ({
  city, phone, address, placeId,
}) => (
  <div className="w-full">
    {/* Map */}
    <div className="w-full aspect-1 aspect-w-1 aspect-h-1">
      <iframe
        title={`奧立佛X竺居聯合設計 ${city}`}
        loading="lazy"
        width="100%"
        className="aspect-1"
        src={`https://www.google.com/maps/embed/v1/place?q=place_id:${placeId}&key=AIzaSyC5Z7OEj85Mu-wg1MJiR5ev5LdSD1MmgCw&zoom=18`}
      />
    </div>
    {/* Info */}
    <div className="w-full p-4 sm:p-6 lg:p-10 -mt-20">
      <div className="border-[3px] border-black relative bg-white">
        {/* For larger desktops */}
        <div className="hidden lg:grid grid-rows-2 divide-y-2 divide-black px-4">
          {/* 1st row */}
          <div className="grid grid-cols-2 divide-x-2 divide-black py-4">
            {/* Office */}
            <div className="leading-loose">
              <div className="uppercase text-gray-500 text-sm">office of</div>
              <div className="">{city}</div>
            </div>
            {/* Phone */}
            <div className="pl-4 leading-loose">
              <div className="uppercase text-gray-500 text-sm">phone</div>
              <div className="">{phone}</div>
            </div>
          </div>
          {/* 2nd row */}
          <div className="py-4">
            {/* Address */}
            <div className="leading-loose">
              <div className="uppercase text-gray-500 text-sm">address</div>
              <div className="">{address}</div>
            </div>
          </div>
        </div>
        {/* For smaller desktops */}
        <div className="grid lg:hidden grid-rows-3 divide-y-2 divide-black px-4">
          {/* Office */}
          <div className="leading-loose py-2">
            <div className="uppercase text-gray-500 text-sm">office of</div>
            <div className="">{city}</div>
          </div>
          {/* Phone */}
          <div className="leading-loose py-2">
            <div className="uppercase text-gray-500 text-sm">phone</div>
            <div className="">{phone}</div>
          </div>
          {/* Address */}
          <div className="leading-loose py-2">
            <div className="uppercase text-gray-500 text-sm">address</div>
            <div className="">{address}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const LocationsSection = () => (
  <div className="">
    <GenericDividingTitle>服務據點</GenericDividingTitle>
    <div className="w-full max-w-lg md:max-w-5xl mx-auto py-3 sm:py-6 px-4 sm:px-0">
      <div className="grid grid-cols-1 md:grid-cols-2 border-[3px] border-black divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
        {/* Kaohsiung */}
        <OfficeLocationItem
          city="高雄・Kaohsiung"
          phone="(07) 2229-568"
          address="高雄市新興區中正三31號2樓"
          placeId="ChIJYTOSA5IEbjQRkpgsGmSYY_Y"
        />
        {/* Taipei */}
        <OfficeLocationItem
          city="台北・Taipei"
          phone="(02) 2737-2727"
          address="台北市信義區嘉興街127巷4號1樓"
          placeId="ChIJKVgoGTOrQjQR-IgK6UJ8DY4"
        />
      </div>
    </div>
  </div>
);

interface SocialMediaItemProps {
  link:
  | 'https://www.facebook.com/KHinteriordesign'
  | 'https://www.instagram.com/oliverdesign_x_revieworks'
  | 'https://www.youtube.com/channel/UCXawb804S47B3ZVsBQM4raA'
  | 'https://medium.com/oliver-interior-design',
  name: 'Facebook' | 'Instagram' | 'YouTube' | 'Medium',
  icon: ReactNode,
}
const SocialMediaItem: FC<SocialMediaItemProps> = ({ link, name, icon }) => (
  <a
    rel="noreferrer"
    href={link}
    target="_blank"
    className={clsx(
      'p-4 space-y-4 lg:space-y-2',
      'border border-black aspect-1',
      'flex flex-col items-center justify-center',
      'hover:bg-gray-100 duration-150',
    )}
  >
    <div className="p-4 lg:p-2 pb-0 w-full">{icon}</div>
    <span className="text-xl">{name}</span>
  </a>
);

const FollowUsSection = () => (
  <div className="">
    <GenericDividingTitle>Follow Us</GenericDividingTitle>
    <div className="py-6 px-4 sm:px-0 text-center">
      歡迎關注我們的 Facebook 與 Medium 部落格，定期更新作品案例、設計概念！
    </div>
    <div className="w-full max-w-md md:max-w-2xl mx-auto px-4 sm:px-0">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-4">
        <SocialMediaItem
          name="Facebook"
          link="https://www.facebook.com/KHinteriordesign"
          icon={(
            <Image
              alt="Facebook"
              layout="responsive"
              src={facebookIcon}
            />
          )}
        />
        <SocialMediaItem
          name="Instagram"
          link="https://www.instagram.com/oliverdesign_x_revieworks"
          icon={(
            <Image
              alt="Instagram"
              layout="responsive"
              src={instagramIcon}
            />
          )}
        />
        <SocialMediaItem
          name="YouTube"
          link="https://www.youtube.com/channel/UCXawb804S47B3ZVsBQM4raA"
          icon={(
            <Image
              alt="YouTube"
              layout="responsive"
              src={youtubeIcon}
            />
          )}
        />
        <SocialMediaItem
          name="Medium"
          link="https://medium.com/oliver-interior-design"
          icon={(
            <Image
              alt="Medium"
              layout="responsive"
              src={mediumIcon}
            />
          )}
        />
      </div>
    </div>
  </div>
);

const Page = () => (
  <>
    <Title>聯絡我們</Title>
    <NavBar />
    <div className="sm:container mx-auto mb-20 py-4 sm:py-2 space-y-4">
      <ContactUsSection />
      <LocationsSection />
      <FollowUsSection />
    </div>
    <Footer />
  </>
);

export default Page;
